import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Col, FormGroup, Label, Input, Alert, Table, Row } from 'reactstrap';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useShortenPcbSn } from './hooks/useShortenPcbSn';

export function GraphView() {
    const history = useHistory();
    const location = useLocation();

    const { selectedBoards = [], selectedDataKeys = [] } = location.state || {};

    const [fromTimestamp, setFromTimestamp] = useState(0);
    const [toTimestamp, setToTimestamp] = useState(0);
    const [graphData, setGraphData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [activeSeries, setActiveSeries] = useState(new Set());

    // Axis tick controls
    const [yAxisTicks, setYAxisTicks] = useState(8);
    const [xAxisTicks, setXAxisTicks] = useState(12);

    // Y-Axis controls
    const [yMin, setYMin] = useState('');
    const [yMax, setYMax] = useState('');
    const [yLabel, setYLabel] = useState('');

    // X-Axis label format style
    const [xLabelStyle, setXLabelStyle] = useState('short');

    // UTC to Local offset (hours)
    const [hoursFromUTC, setHoursFromUTC] = useState(0);

    const hasInitialLoaded = useRef(false);

    const token = localStorage.getItem("token");

    // Time picker state (UTC)
    const [fromYear, setFromYear] = useState('');
    const [fromMon, setFromMon] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [fromHrs, setFromHrs] = useState('');
    const [fromMin, setFromMin] = useState('');
    const [fromSec, setFromSec] = useState('');

    const [toYear, setToYear] = useState('');
    const [toMon, setToMon] = useState('');
    const [toDate, setToDate] = useState('');
    const [toHrs, setToHrs] = useState('');
    const [toMin, setToMin] = useState('');
    const [toSec, setToSec] = useState('');

    const { shortenPcbSn } = useShortenPcbSn();

    const deviceIds = [...new Set(selectedBoards.map(b => b.idImpDevice).filter(Boolean))];

    // Initialize active series
    useEffect(() => {
        const initialActive = new Set();
        selectedBoards.forEach(board => {
            const shortSn = shortenPcbSn(board.pcbSn);
            selectedDataKeys.forEach(key => {
                initialActive.add(`${shortSn}_${key}`);
            });
        });
        setActiveSeries(initialActive);
    }, [selectedBoards, selectedDataKeys]);

    // Initial load: set default time range
    useEffect(() => {
        if (deviceIds.length === 0 || hasInitialLoaded.current) return;

        hasInitialLoaded.current = true;

        const fetchMostRecentData = async () => {
            try {
                const recentData = await Promise.all(
                    selectedBoards.map(async (board) => {
                        const resp = await fetch(`api/boardData/pcbSn/${board.pcbSn}/1`, {
                            method: "GET",
                            headers: { "Authorization": `Bearer ${token}` }
                        });
                        if (resp.ok) {
                            const data = await resp.json();
                            return data[0];
                        }
                        return null;
                    })
                );

                let latestTimeReceived = 0;
                recentData.forEach(record => {
                    if (record && record.timeReceived) {
                        let ts = record.timeReceived;
                        if (typeof ts === 'string') {
                            ts = new Date(ts).getTime() / 1000;
                        } else {
                            ts = Number(ts);
                        }
                        ts = Math.round(ts);
                        if (ts > latestTimeReceived) latestTimeReceived = ts;
                    }
                });

                if (latestTimeReceived === 0) {
                    latestTimeReceived = Math.round(Date.now() / 1000);
                }

                const fromTime = latestTimeReceived - (15 * 86400);

                setToTimestamp(latestTimeReceived);
                setFromTimestamp(fromTime);
                populateTimeFields(fromTime, latestTimeReceived);

            } catch (err) {
                console.error("Failed to fetch most recent data", err);
                const now = Math.round(Date.now() / 1000);
                const fromTime = now - 15 * 86400;
                setToTimestamp(now);
                setFromTimestamp(fromTime);
                populateTimeFields(fromTime, now);
            }
        };

        fetchMostRecentData();
    }, [deviceIds, selectedBoards, token]);

    const populateTimeFields = (fromTs, toTs) => {
        const fromDateObj = new Date(fromTs * 1000);
        setFromYear(fromDateObj.getUTCFullYear());
        setFromMon(fromDateObj.getUTCMonth() + 1);
        setFromDate(fromDateObj.getUTCDate());
        setFromHrs(fromDateObj.getUTCHours());
        setFromMin(fromDateObj.getUTCMinutes());
        setFromSec(fromDateObj.getUTCSeconds());

        const toDateObj = new Date(toTs * 1000);
        setToYear(toDateObj.getUTCFullYear());
        setToMon(toDateObj.getUTCMonth() + 1);
        setToDate(toDateObj.getUTCDate());
        setToHrs(toDateObj.getUTCHours());
        setToMin(toDateObj.getUTCMinutes());
        setToSec(toDateObj.getUTCSeconds());
    };

    const getTimestampsFromInputs = () => {
        const fromTs = Math.floor(Date.UTC(
            parseInt(fromYear), parseInt(fromMon) - 1, parseInt(fromDate),
            parseInt(fromHrs), parseInt(fromMin), parseInt(fromSec)
        ) / 1000);

        const toTs = Math.floor(Date.UTC(
            parseInt(toYear), parseInt(toMon) - 1, parseInt(toDate),
            parseInt(toHrs), parseInt(toMin), parseInt(toSec)
        ) / 1000);

        return { fromTs, toTs };
    };

    const handleRefresh = () => {
        const { fromTs, toTs } = getTimestampsFromInputs();

        if (isNaN(fromTs) || isNaN(toTs) || toTs <= fromTs) {
            setError("Invalid time range. 'To' must be after 'From'.");
            return;
        }

        setFromTimestamp(fromTs);
        setToTimestamp(toTs);
        setError(null);
        setLoading(true);
    };

    useEffect(() => {
        if (fromTimestamp > 0 && toTimestamp > 0 && toTimestamp > fromTimestamp) {
            loadGraphData();
        }
    }, [fromTimestamp, toTimestamp]);

    const loadGraphData = async () => {
        if (deviceIds.length === 0) {
            setError("No valid Device IDs found.");
            setLoading(false);
            return;
        }
        if (toTimestamp <= fromTimestamp) {
            setError("To date must be after From date.");
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        setGraphData([]);          // ← Clear data IMMEDIATELY to prevent old data flash

        try {
            const resp = await fetch(
                `api/boarddata/limits?notbefore=${Math.floor(fromTimestamp)}&until=${Math.floor(toTimestamp)}&offset=0&limit=10100`,
                {
                    method: 'POST',
                    body: JSON.stringify(deviceIds),
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-type": "application/json"
                    }
                }
            );

            if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);

            const rawData = await resp.json();

            const timeSeries = {};
            rawData.forEach(entry => {
                let ts = entry.timeSent || entry.timeReceived;
                if (!ts) return;

                if (typeof ts === 'string') {
                    ts = Math.floor(new Date(ts).getTime() / 1000);
                } else {
                    ts = Math.floor(Number(ts));
                }

                if (isNaN(ts) || !isFinite(ts)) return;

                if (!timeSeries[ts]) timeSeries[ts] = { timestamp: ts };

                selectedDataKeys.forEach(key => {
                    if (entry.dataList?.[key] !== undefined) {
                        const value = Number(entry.dataList[key]);
                        if (!isNaN(value) && isFinite(value)) {
                            const seriesKey = `${shortenPcbSn(entry.pcbSn)}_${key}`;
                            if (activeSeries.has(seriesKey)) {
                                timeSeries[ts][seriesKey] = value;
                            }
                        }
                    }
                });
            });

            const formatted = Object.values(timeSeries).sort((a, b) => a.timestamp - b.timestamp);
            setGraphData(formatted);

        } catch (err) {
            setError(err.message || "Failed to load graph data.");
            setGraphData([]);          // Clear on error
        } finally {
            setLoading(false);
        }
    };

    // Helper: Determine if a date is in Daylight Saving Time (simple heuristic)
    const isDaylightSavingTime = (date) => {
        const jan = new Date(date.getFullYear(), 0, 1).getTimezoneOffset();
        const jul = new Date(date.getFullYear(), 6, 1).getTimezoneOffset();
        return Math.max(jan, jul) !== date.getTimezoneOffset();
    };

    // Two separate maps: Standard Time and Daylight Time (common global abbreviations)
    const getTimezoneAbbr = (offset) => {
        const stdMap = {
            "-12": "UTC-12", "-11": "UTC-11", "-10": "HST",
            "-9": "AKST", "-8": "PST", "-7": "MST",
            "-6": "CST", "-5": "EST", "-4": "AST",
            "-3": "ADT", "-2": "UTC-2", "-1": "UTC-1",
            "0": "UTC",
            "1": "CET", "2": "EET", "3": "EET",
            "4": "UTC+4", "5": "UTC+5", "6": "UTC+6",
            "7": "UTC+7", "8": "CST", "9": "JST",
            "10": "AEST", "11": "UTC+11", "12": "UTC+12"
        };

        const dstMap = {
            "-12": "UTC-12", "-11": "UTC-11", "-10": "CKT",
            "-9": "HDT" ,"-8": "AKDT", "-7": "PDT", "-6": "MDT",
            "-5": "CDT", "-4": "EDT", "-3": "AST",
            "-2": "ADT", "-1": "UTC-1",
            "0": "UTC",
            "1": "BST", "2": "CAT", "3": "MSK",
            "4": "SAMT", "5": "YEKT", "6": "ALMT",
            "7": "NOVST", "8": "AWST", "9": "JST",
            "10": "AEDT", "11": "AEDT", "12": "NZST"
        };

        const offsetStr = offset.toString();
        const adjustedDate = new Date(Date.now() + offset * 3600000);

        return isDaylightSavingTime(adjustedDate)
            ? (dstMap[offsetStr] || `UTC${offset >= 0 ? '+' : ''}${offset}`)
            : (stdMap[offsetStr] || `UTC${offset >= 0 ? '+' : ''}${offset}`);
    };

    // X-Axis tick formatter with UTC offset
    const formatXAxisTick = (tickValue) => {
        const adjustedTimestamp = tickValue + (hoursFromUTC * 3600);
        const date = new Date(adjustedTimestamp * 1000);

        switch (xLabelStyle) {
            case 'short': {
                return date.toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                }).replace(',', ' at');
            }
            case 'weekday': {
                return date.toLocaleString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                }).replace(',', ',');
            }
            case 'mdy': {
                const mdy = `${date.getUTCMonth() + 1}-${date.getUTCDate()}-${date.getUTCFullYear()}`;
                const time12 = date.toLocaleString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                });
                return `${mdy} at ${time12}`;
            }
            case 'iso': {
                const year = date.getUTCFullYear();
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const day = String(date.getUTCDate()).padStart(2, '0');
                const hour = String(date.getUTCHours()).padStart(2, '0');
                const minute = String(date.getUTCMinutes()).padStart(2, '0');
                return `${year}-${month}-${day} ${hour}:${minute}`;
            }
            default: {
                return date.toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                });
            }
        }
    };

    const toggleSeries = (seriesKey) => {
        setActiveSeries(prev => {
            const newSet = new Set(prev);
            if (newSet.has(seriesKey)) newSet.delete(seriesKey);
            else newSet.add(seriesKey);
            return newSet;
        });
        setTimeout(() => loadGraphData(), 10);
    };

    const allSeriesKeys = [];
    selectedBoards.forEach(board => {
        const shortSn = shortenPcbSn(board.pcbSn);
        selectedDataKeys.forEach(key => allSeriesKeys.push(`${shortSn}_${key}`));
    });

    if (selectedBoards.length === 0 || selectedDataKeys.length === 0) {
        return (
            <Col sm="12" md={{ size: 8, offset: 2 }}>
                <Alert color="danger">
                    No boards or fields selected.
                    <Button color="link" onClick={() => history.goBack()}>Go Back</Button>
                </Alert>
            </Col>
        );
    }

    return (
        <Col sm="12" className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mb-0">Graph View</h2>
                <div>
                    <Button color="primary" onClick={handleRefresh} disabled={loading} className="me-2">
                        {loading ? "Loading Data..." : "Refresh Graph"}
                    </Button>
                    <Button color="secondary" onClick={() => history.goBack()}>
                        Back to Selection
                    </Button>
                </div>
            </div>

            {/* Selected Boards & Fields Table */}
            <div className="mb-4">
                <h5>Selected Boards &amp; Fields — Click a cell to toggle the line</h5>
                <Table bordered size="sm" className="data-fields-table">
                    <thead>
                        <tr>
                            <th style={{ width: '180px' }}>Board (PcbSn)</th>
                            {selectedDataKeys.map(key => <th key={key}>{key}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {selectedBoards.map(board => {
                            const shortSn = shortenPcbSn(board.pcbSn);
                            return (
                                <tr key={board.id}>
                                    <td><span className="code">{shortSn}</span></td>
                                    {selectedDataKeys.map(key => {
                                        const seriesKey = `${shortSn}_${key}`;
                                        const isActive = activeSeries.has(seriesKey);
                                        return (
                                            <td
                                                key={key}
                                                style={{
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    backgroundColor: isActive ? '#cfe2ff' : '#f8f9fa',
                                                    fontWeight: isActive ? '600' : 'normal'
                                                }}
                                                onClick={() => toggleSeries(seriesKey)}
                                            >
                                                {isActive ? '✓' : ' '}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>

            {/* Four-column layout */}
            <div className="mb-4">
                <Row className="align-items-start">
                    {/* From (UTC) */}
                    <Col md={3}>
                        <Label className="mb-2"><strong>From (UTC)</strong></Label>
                        <div className="d-flex gap-2 flex-wrap">
                            <div><div className="small text-muted mb-1">Year</div><Input type="text" value={fromYear} onChange={e => setFromYear(e.target.value)} maxLength={4} style={{ width: '80px' }} /></div>
                            <div><div className="small text-muted mb-1">Month</div><Input type="text" value={fromMon} onChange={e => setFromMon(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                            <div><div className="small text-muted mb-1">Day</div><Input type="text" value={fromDate} onChange={e => setFromDate(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                            <div><div className="small text-muted mb-1">Hr</div><Input type="text" value={fromHrs} onChange={e => setFromHrs(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                            <div><div className="small text-muted mb-1">Min</div><Input type="text" value={fromMin} onChange={e => setFromMin(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                            <div><div className="small text-muted mb-1">Sec</div><Input type="text" value={fromSec} onChange={e => setFromSec(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                        </div>
                    </Col>

                    {/* To (UTC) */}
                    <Col md={3}>
                        <Label className="mb-2"><strong>To (UTC)</strong></Label>
                        <div className="d-flex gap-2 flex-wrap">
                            <div><div className="small text-muted mb-1">Year</div><Input type="text" value={toYear} onChange={e => setToYear(e.target.value)} maxLength={4} style={{ width: '80px' }} /></div>
                            <div><div className="small text-muted mb-1">Month</div><Input type="text" value={toMon} onChange={e => setToMon(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                            <div><div className="small text-muted mb-1">Day</div><Input type="text" value={toDate} onChange={e => setToDate(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                            <div><div className="small text-muted mb-1">Hr</div><Input type="text" value={toHrs} onChange={e => setToHrs(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                            <div><div className="small text-muted mb-1">Min</div><Input type="text" value={toMin} onChange={e => setToMin(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                            <div><div className="small text-muted mb-1">Sec</div><Input type="text" value={toSec} onChange={e => setToSec(e.target.value)} maxLength={2} style={{ width: '60px' }} /></div>
                        </div>
                    </Col>

                    {/* X-Axis Label Style */}
                    <Col md={3}>
                        <Label className="mb-2"><strong>X-Axis Date/Time Label Style</strong></Label>
                        <div className="d-flex flex-column gap-2">
                            <FormGroup check>
                                <Label check>
                                    <Input type="radio" name="xLabelStyle" value="short" checked={xLabelStyle === 'short'} onChange={(e) => setXLabelStyle(e.target.value)} />{' '}
                                    Sep 15 at 6:00 PM
                                </Label>
                            </FormGroup>
                            <FormGroup check>
                                <Label check>
                                    <Input type="radio" name="xLabelStyle" value="weekday" checked={xLabelStyle === 'weekday'} onChange={(e) => setXLabelStyle(e.target.value)} />{' '}
                                    Mon, Sep 15 at 6:00 PM
                                </Label>
                            </FormGroup>
                            <FormGroup check>
                                <Label check>
                                    <Input type="radio" name="xLabelStyle" value="mdy" checked={xLabelStyle === 'mdy'} onChange={(e) => setXLabelStyle(e.target.value)} />{' '}
                                    7-15-2025 at 6:00 PM
                                </Label>
                            </FormGroup>
                            <FormGroup check>
                                <Label check>
                                    <Input type="radio" name="xLabelStyle" value="iso" checked={xLabelStyle === 'iso'} onChange={(e) => setXLabelStyle(e.target.value)} />{' '}
                                    2025-07-15 18:00
                                </Label>
                            </FormGroup>
                        </div>
                    </Col>

                    {/* Convert UTC to Local */}
                    <Col md={3}>
                        <Label className="mb-2"><strong>Convert UTC to Local</strong></Label>
                        <div className="mb-1">
                            <strong>{hoursFromUTC >= 0 ? '+' : ''}{hoursFromUTC}</strong> hours from UTC
                        </div>
                        <div className="mb-3 text-primary fw-bold fs-5">
                            {getTimezoneAbbr(hoursFromUTC)}
                        </div>
                        <input
                            type="range"
                            min="-12"
                            max="12"
                            step="1"
                            value={hoursFromUTC}
                            onChange={(e) => setHoursFromUTC(parseInt(e.target.value))}
                            className="form-range"
                        />
                    </Col>
                </Row>
            </div>

            {/* Y-Axis section */}
            <div className="mb-4">
                <h5>Y-Axis</h5>
                <Row className="align-items-end">
                    <Col md={2}>
                        <Label>Y-Min</Label>
                        <Input
                            type="number"
                            value={yMin}
                            onChange={e => setYMin(e.target.value)}
                            placeholder="Auto"
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col md={2}>
                        <Label>Y-Max</Label>
                        <Input
                            type="number"
                            value={yMax}
                            onChange={e => setYMax(e.target.value)}
                            placeholder="Auto"
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col md={4}>
                        <Label>Y-Label</Label>
                        <Input
                            type="text"
                            value={yLabel}
                            onChange={e => setYLabel(e.target.value)}
                            placeholder="Y Axis Label (optional)"
                        />
                    </Col>
                    <Col md={4} />
                </Row>
            </div>

            {/* Axis Ticks Sliders */}
            <div className="mb-4">
                <h5>Graph Axis Density</h5>
                <Row className="align-items-center">
                    <Col md={6}>
                        <Label className="mb-2">
                            Y-Axis Ticks: <strong>{yAxisTicks}</strong>
                        </Label>
                        <input
                            type="range"
                            min="4"
                            max="20"
                            step="1"
                            value={yAxisTicks}
                            onChange={(e) => setYAxisTicks(parseInt(e.target.value))}
                            className="form-range"
                        />
                    </Col>

                    <Col md={6}>
                        <Label className="mb-2">
                            X-Axis Ticks: <strong>{xAxisTicks}</strong>
                        </Label>
                        <input
                            type="range"
                            min="6"
                            max="30"
                            step="1"
                            value={xAxisTicks}
                            onChange={(e) => setXAxisTicks(parseInt(e.target.value))}
                            className="form-range"
                        />
                    </Col>
                </Row>
            </div>

            {error && <Alert color="danger" className="mt-3">{error}</Alert>}

            {/* Graph Rendering Area */}
            {graphData.length > 0 && !loading ? (
                <div className="mb-5">
                    <h5>Graph — Click cells above to toggle individual lines</h5>
                    <ResponsiveContainer width="100%" height={910}>
                        <LineChart data={graphData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="timestamp"
                                type="number"
                                domain={['auto', 'auto']}
                                tickFormatter={formatXAxisTick}
                                angle={-45}
                                textAnchor="end"
                                height={100}
                                tick={{ fontSize: 11 }}
                                tickCount={xAxisTicks}
                            />
                            <YAxis
                                tickCount={yAxisTicks}
                                domain={yMin !== '' && yMax !== '' ? [Number(yMin), Number(yMax)] : ['auto', 'auto']}
                                allowDataOverflow={true}
                                label={yLabel ? { value: yLabel, angle: -90, position: 'left' } : undefined}
                            />
                            <Tooltip labelFormatter={(v) => new Date((v + hoursFromUTC * 3600) * 1000).toLocaleString('en-US')} />
                            <Legend />
                            {allSeriesKeys
                                .filter(key => activeSeries.has(key))
                                .map((seriesKey, i) => (
                                    <Line
                                        key={seriesKey}
                                        type="monotone"
                                        dataKey={seriesKey}
                                        stroke={`hsl(${i * 25}, 70%, 50%)`}
                                        strokeWidth={2.5}
                                        dot={{ r: 3 }}
                                        name={seriesKey}
                                    />
                                ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            ) : loading ? (
                <Alert color="info" className="mt-4">
                    <strong>Loading new data for the selected time range...</strong><br />
                    <small>This may take 30 seconds or more depending on the date range.</small>
                </Alert>
            ) : (
                <Alert color="warning" className="mt-3">
                    No data found in the selected time range for the currently active fields.
                </Alert>
            )}
        </Col>
    );
}